import { useState, useEffect } from "react";
import { supabase } from "../../../supabaseClient";
import { getDocsBucket } from "../../../components/eventStorageConfig";
import "./Step3.css";

export default function Step3Upload({ vendor, event, app, formData, setFormData, setCanProceed }) {
  const eventKey = event?.event_key || event?.id;
  const bucket   = getDocsBucket(eventKey);

  const [uploadingFile, setUploadingFile] = useState({ indemnity: false, agreement: false });
  const [deletingFile, setDeletingFile]   = useState(null);
  const [generatingLink, setGeneratingLink] = useState(null);

  /* =========================================================
     HYDRATE EXISTING UPLOADS FROM THE ALREADY-LOADED app RECORD
     (vendor + app are passed in from upstream — nothing to fetch here)
  ========================================================= */
  useEffect(() => {
    const existingUploads = {};

    if (app?.indemnity_path) {
      existingUploads.indemnity = { name: app.indemnity_name || "Indemnity form", path: app.indemnity_path };
    }
    if (app?.agreement_path) {
      existingUploads.agreement = { name: app.agreement_name || "Agreement form", path: app.agreement_path };
    }

    if (Object.keys(existingUploads).length > 0) {
      setFormData((prev) => ({ ...prev, uploads: { ...existingUploads, ...prev.uploads } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app?.id]);

  /* =========================================================
     HELPERS
  ========================================================= */
  const sanitizeFolderName = (name) =>
    name
      ?.normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_-]/g, "");

  /* =========================================================
     HANDLE FILE UPLOAD
  ========================================================= */
  const handleUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!bucket) {
      alert("Document upload for this event isn't set up yet — please check back soon.");
      return;
    }

    if (!vendor?.brand_name) {
      alert("Brand name not found.");
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      alert("Your session has expired. Please refresh the page and log back in.");
      return;
    }

    setUploadingFile((prev) => ({ ...prev, [type]: true }));

    try {
      const safeBrand = sanitizeFolderName(vendor.brand_name);
      const prefix    = type === "agreement" ? "vendor_agreement" : "indemnity_form";

      const safeFileName = file.name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9._-]/g, "_");

      const fileName = `${prefix}_${Date.now()}_${safeFileName}`;
      const filePath = `${safeBrand}/${fileName}`;

      const { error: uploadErr } = await supabase.storage.from(bucket).upload(filePath, file, { upsert: true });
      if (uploadErr) throw uploadErr;

      if (app?.id) {
        const column = type === "indemnity"
          ? { indemnity_path: filePath, indemnity_name: file.name }
          : { agreement_path: filePath, agreement_name: file.name };

        const { error: updateErr } = await supabase
          .from("vendor_applications")
          .update(column)
          .eq("id", app.id);

        if (updateErr) console.warn("Failed to persist upload path:", updateErr);
      }

      setFormData((prev) => ({
        ...prev,
        uploads: { ...prev.uploads, [type]: { name: file.name, path: filePath } },
      }));
    } catch (err) {
      console.error("Full upload error:", err);
      alert("Upload failed: " + err.message);
    } finally {
      setUploadingFile((prev) => ({ ...prev, [type]: false }));
    }
  };

  /* =========================================================
     DELETE FILE FROM STORAGE + CLEAR FROM DB
  ========================================================= */
  const handleDelete = async (type, path) => {
    if (!path || !bucket) return;

    const confirmDelete = window.confirm("Are you sure you want to remove this file?");
    if (!confirmDelete) return;

    setDeletingFile(type);

    try {
      const { error: storageErr } = await supabase.storage.from(bucket).remove([path]);
      if (storageErr) throw storageErr;

      if (app?.id) {
        const column = type === "indemnity"
          ? { indemnity_path: null, indemnity_name: null }
          : { agreement_path: null, agreement_name: null };

        await supabase.from("vendor_applications").update(column).eq("id", app.id);
      }

      setFormData((prev) => {
        const updatedUploads = { ...prev.uploads };
        delete updatedUploads[type];
        return { ...prev, uploads: updatedUploads };
      });
    } catch (err) {
      alert("Failed to delete file.");
      console.error(err);
    } finally {
      setDeletingFile(null);
    }
  };

  /* =========================================================
     GENERATE SIGNED URL FOR VIEW
  ========================================================= */
  const handleView = async (path, key) => {
    if (!path || !bucket) return;

    setGeneratingLink(key);

    try {
      const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 60 * 5);
      if (error) throw error;
      window.open(data.signedUrl, "_blank");
    } catch (err) {
      alert("Unable to generate secure link.");
    } finally {
      setGeneratingLink(null);
    }
  };

  /* =========================================================
     CAN PROCEED LOGIC
  ========================================================= */
  useEffect(() => {
    const hasBoth = formData.uploads?.indemnity && formData.uploads?.agreement;
    const isUploading = Object.values(uploadingFile).some((v) => v);
    const isDeleting = deletingFile !== null;
    setCanProceed(hasBoth && !isUploading && !isDeleting);
  }, [formData.uploads, uploadingFile, deletingFile, setCanProceed]);

  /* =========================================================
     RENDER
  ========================================================= */
  if (!bucket) {
    return (
      <div className="onboarding-step step3-upload">
        <h3>Step 3: Upload Signed Forms 📄</h3>
        <p>Document upload for {event?.name || "this event"} isn't set up yet — please check back soon. You can continue and come back to this step later.</p>
      </div>
    );
  }

  return (
    <div className="onboarding-step step3-upload">
      <h3>Step 3: Upload Signed Forms 📄</h3>
      <p>Please upload <strong>BOTH</strong> signed documents to continue.</p>

      <div className="upload-section">
        <label className="upload-btn">
          {uploadingFile.indemnity
            ? "Uploading..."
            : formData.uploads?.indemnity
            ? "Indemnity ✅ — click to replace"
            : "Upload Vendor Insurance & Indemnity Declaration"}
          <input
            type="file"
            onChange={(e) => handleUpload(e, "indemnity")}
            disabled={uploadingFile.indemnity || !vendor?.brand_name}
            hidden
          />
        </label>

        <label className="upload-btn">
          {uploadingFile.agreement
            ? "Uploading..."
            : formData.uploads?.agreement
            ? "Agreement ✅ — click to replace"
            : "Upload Vendor Participant Agreement & Indemnity"}
          <input
            type="file"
            onChange={(e) => handleUpload(e, "agreement")}
            disabled={uploadingFile.agreement || !vendor?.brand_name}
            hidden
          />
        </label>
      </div>

      <div className="upload-preview">
        {formData.uploads &&
          Object.entries(formData.uploads).map(([key, file]) => (
            <div key={key} className="preview-item">
              ✅ {file.name} —{" "}
              <button className="view-btn" onClick={() => handleView(file.path, key)} disabled={generatingLink === key}>
                {generatingLink === key ? "Generating..." : "View"}
              </button>
              <button className="delete-btn" onClick={() => handleDelete(key, file.path)} disabled={deletingFile === key}>
                {deletingFile === key ? "..." : "✕"}
              </button>
            </div>
          ))}
      </div>
    </div>
  );
}